import React, { useEffect, useMemo } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { useAuth } from 'shared/providers/AuthProvider';
import useClinicUser from 'shared/hooks/useClinicUser';
import DefaultAuthenticated from 'shared/components/Layouts/DefaultAuthenticated';
import { useGetClinicChannelStatusesQuery } from 'shared/types/graphql';
import useChurnZero from 'shared/hooks/useChurnZero';
import useHotJar from 'shared/hooks/useHotjar';
import { GraphQLFetchPolicies } from 'shared/enums';
import { env } from 'env';
import { RouteDefinitions } from 'routes/types/Routes';
import { ModuleName } from '@televet/shared-types/productPackages';
import { Box } from '@televet/kibble-ui';
import { useAppSelector } from 'state/hooks';
import { useModularity } from 'shared/hooks/useModularity';
import { RouteBasePaths } from '../types/Routes';
import Conversations from 'pages/Conversations';
import Team from 'pages/Team';
import Settings from 'pages/Settings';
import Forms from 'pages/Forms';
import WorkflowsDemo from 'pages/Demo/WorkflowsDemo';
import MessageTemplates from 'pages/MessageTemplates';
import EmailTemplates from 'pages/EmailTemplates';
import Reporting from 'pages/Reporting';
import Board from 'pages/Board';
import Survey from 'pages/Survey';
import LoadingAndPresentation from 'routes/components/LoadingAndPresentation';
import { LoyaltyProgram } from 'pages/LoyaltyProgram';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import { ChannelMessageAttachment } from 'pages/ChannelMessageAttachement/ChannelMessageAttachment';
import SuccessCenter from 'pages/SuccessCenter/SuccessCenter';
import Automations from 'pages/Automations';
import OttoEmailCampaigns from 'pages/OttoEmailCampaigns';
import GlobalAutomations from 'pages/GlobalAutomations';
import Tasks from 'pages/Tasks';
import AppointmentsRouter from 'pages/Appointments/AppointmentsRouter';

const protectedRoutes = [
  {
    path: RouteDefinitions.Appointments,
    component: AppointmentsRouter,
    modules: [ModuleName.AppointmentsStandard, ModuleName.AppointmentsRecap],
  },
  { path: RouteDefinitions.Board, component: Board, modules: [ModuleName.AppointmentsStandard] },
  {
    path: RouteDefinitions.Conversations,
    component: Conversations,
    modules: [ModuleName.AppointmentsStandard],
  },
  { path: RouteDefinitions.Team, component: Team, modules: [ModuleName.TeamChat] },
  {
    path: RouteDefinitions.Settings,
    component: Settings,
    modules: [ModuleName.AppointmentsStandard, ModuleName.AppointmentsRecap],
  },
  {
    path: RouteDefinitions.Automations,
    component: Automations,
    modules: [ModuleName.AppointmentsStandard],
  },
  {
    path: RouteDefinitions.MessageTemplates,
    component: MessageTemplates,
    modules: [ModuleName.AppointmentsStandard],
  },
  {
    path: RouteDefinitions.Forms,
    exact: true,
    component: Forms,
    modules: [ModuleName.AppointmentsStandard],
  },
  {
    path: RouteDefinitions.DemoWorkflow,
    exact: true,
    component: WorkflowsDemo,
    modules: [ModuleName.AppointmentsStandard],
  },
  {
    path: RouteDefinitions.EmailTemplates,
    component: EmailTemplates,
    modules: [ModuleName.AppointmentsStandard],
  },
  {
    path: RouteDefinitions.Reporting,
    component: Reporting,
    modules: [ModuleName.AppointmentsStandard],
  },
  {
    path: RouteDefinitions.Survey,
    component: Survey,
    modules: [ModuleName.AppointmentsStandard],
  },
  {
    path: RouteDefinitions.SuccessCenter,
    component: SuccessCenter,
    modules: [ModuleName.AppointmentsStandard],
  },
  {
    path: RouteDefinitions.GlobalAutomations,
    component: GlobalAutomations,
    modules: [ModuleName.AppointmentsStandard],
  },
  {
    path: RouteDefinitions.OttoEmailCampaigns,
    component: OttoEmailCampaigns,
    modules: [ModuleName.AppointmentsStandard],
  },
  {
    path: RouteDefinitions.ChannelMessageAttachments,
    component: ChannelMessageAttachment,
    modules: [ModuleName.AppointmentsStandard],
  },
  {
    path: RouteDefinitions.LoyaltyProgram,
    component: LoyaltyProgram,
    featureFlag: FeatureFlagName.LoyaltyProgram,
    modules: [ModuleName.AppointmentsStandard],
  },
  { path: RouteDefinitions.Tasks, component: Tasks, modules: [ModuleName.AppointmentsStandard] },
];

const ProtectedLayout = (): JSX.Element | null => {
  const { isAuthenticated, isLoggingOut } = useAuth();
  const { pathname, search } = useLocation();
  const { isFeatureEnabled, isFeatureFlagsLoading } = useFeatureFlag();
  const { clinicUser, currentClinic, currentClinicId, isTeletriageAgent } = useClinicUser();
  const { modules, hasModule } = useModularity();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const defaultRedirect = useAppSelector((state) => state.currentClinic.defaultRedirect);

  const { identifyUser: churnZeroIdentifyUser } = useChurnZero();

  const { data: channelStatusesData } = useGetClinicChannelStatusesQuery({
    variables: { clinicId: currentClinicId || '' },
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
    skip: !currentClinicId,
  });

  const { identify: hotjarIdentify } = useHotJar();

  /**
   * Notify ChurnZero when user is identified on initial page load; reset when user logs out
   */
  useEffect(() => {
    if (clinicUser?.id && !isTeletriageAgent) {
      const { email, firstName, lastName } = clinicUser;
      churnZeroIdentifyUser({ email, firstName, lastName, clinicCrmId: currentClinic?.crmId || undefined });
    }
  }, [isAuthenticated, clinicUser, currentClinic, churnZeroIdentifyUser, isTeletriageAgent]);

  /**
   * Notify Hotjar when user is identified on initial page load; reset when user logs out
   */
  useEffect(() => {
    if (isAuthenticated && clinicUser?.id) {
      const numberOfChannelStatuses =
        channelStatusesData?.findManyChannelStatus?.filter((channelStatus) => !channelStatus.deletedOn).length || -1;
      if (numberOfChannelStatuses >= 0) {
        hotjarIdentify({ numberOfChannelStatuses });
      }
    }
  }, [channelStatusesData?.findManyChannelStatus, clinicUser?.id, hotjarIdentify, isAuthenticated]);

  const availableRoutes = useMemo(() => {
    // On initial load before modules and feature flags return, default to showing all routes
    if (!modules.length || isFeatureFlagsLoading) {
      return protectedRoutes;
    }
    return protectedRoutes.filter(({ featureFlag, modules: routeModules }) => {
      if (featureFlag && !isFeatureEnabled(featureFlag)) {
        return false;
      }
      if (!routeModules.some((module) => hasModule(module))) {
        return false;
      }
      return true;
    });
  }, [isFeatureFlagsLoading, modules, hasModule, isFeatureEnabled]);

  // Set redirect route for unauthenticated users
  if (!isAuthenticated && !isLoggingOut) {
    const redirectPath = `?redirect=${pathname}&${search.replace('?', '')}`;
    window.location.href = `${env.REACT_APP_LOGIN_URL}/logout${redirectPath}`;
    return null;
  }

  return (
    <>
      <LoadingAndPresentation />
      <DefaultAuthenticated>
        <Box flex="1 1 auto" overflow="auto" width="100%" className="DefaultAuthenticated__ContentLayout">
          <Switch>
            {availableRoutes.map((route) => (
              <Route key={route.path} path={route.path} exact={route.exact} component={route.component} />
            ))}
            {defaultRedirect && (
              <Redirect
                from="*"
                to={
                  params
                    ? `${defaultRedirect || RouteBasePaths.Conversations}?${params}`
                    : defaultRedirect || RouteBasePaths.Conversations
                }
              />
            )}
          </Switch>
        </Box>
      </DefaultAuthenticated>
    </>
  );
};

export default ProtectedLayout;
